import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Brain, Loader2, Mail, MessageCircle, Award, Lightbulb, BookOpen, Download, ExternalLink, FileText, AlertTriangle, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import AnnotatedScript from "@/components/AnnotatedScript";
import { useAuth } from "@/context/AuthContext";

export default function ResultView() {
  const { scriptId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [feedback, setFeedback] = useState({});

  useEffect(() => {
    api.get(`/answer-scripts/${scriptId}`).then((r) => { setScript(r.data); setFeedback(r.data.feedback || {}); }).catch(() => {});
  }, [scriptId]);

  useEffect(() => {
    if (!["queued", "evaluating"].includes(script?.status)) return;
    let active = true;
    const timer = setInterval(() => {
      api.get(`/answer-scripts/${scriptId}`).then(({ data }) => {
        if (active) setScript(data);
      }).catch(() => {});
    }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [scriptId, script?.status]);

  const vote = async (question, val) => {
    const current = feedback[String(question)];
    const next = current === val ? "none" : val;
    try {
      const { data } = await api.post(`/answer-scripts/${scriptId}/feedback`, { question, vote: next });
      setFeedback(data.feedback || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save feedback");
    }
  };

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const { data } = await api.post(`/answer-scripts/${scriptId}/evaluate`);
      setScript(data);
      toast.success("Evaluation queued. We will alert you when it is ready.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setEvaluating(false);
    }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get(`/answer-scripts/${scriptId}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EvalReport_${scriptId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await err.response.data.text()).detail;
        } catch (_) {
          detail = null;
        }
      }
      toast.error(formatApiErrorDetail(detail) || "Could not generate report");
    }
  };

  if (!script) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const pending = ["queued", "evaluating"].includes(script.status);
  const studentRetryUsed = user?.role === "student" && script.status === "failed" && script.student_retry_used;
  const r = script.result;
  const pct = r?.total_max ? Math.round((100 * r.total_awarded) / r.total_max) : 0;
  const token = localStorage.getItem("token");
  const pdfUrl = `${API}/answer-scripts/${scriptId}/pdf?auth=${token}`;

  const ScannedPanel = () => (
    <div className="bg-background border border-border rounded-lg overflow-hidden lg:sticky lg:top-24">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /><p className="overline text-muted-foreground">Your scanned answers</p></div>
        <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-primary" data-testid="open-scan-newtab"><ExternalLink className="h-4 w-4" /></a>
      </div>
      <iframe title="scanned-answers" src={pdfUrl} className="w-full h-[60vh] lg:h-[72vh] bg-white" data-testid="scanned-pdf-frame" />
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary/40">
      <header className="sticky top-0 z-20 bg-white/70 dark:bg-background/70 backdrop-blur-xl border-b border-border px-6 py-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h1 className="font-heading font-bold text-lg tracking-tight truncate">{script.paper_title}</h1>
        <div className="ml-auto flex items-center gap-2">
          {script.status === "evaluated" && (
            <Button size="sm" className="gap-2 rounded-full" onClick={downloadReport} data-testid="download-report-button">
              <Download className="h-4 w-4" /> Download PDF
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 fade-up">
        <div className="bg-background border border-border rounded-lg p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="overline text-muted-foreground">{script.subject} &middot; {script.student_name}</p>
            <h2 className="font-heading font-black text-2xl tracking-tight mt-1">Evaluation Report</h2>
            {r?.language_detected && <p className="text-sm text-muted-foreground mt-1">Language: {r.language_detected}</p>}
          </div>
          {r && (
            <div className="text-right">
              <p className="font-heading font-black text-5xl text-primary">{r.total_awarded}<span className="text-2xl text-muted-foreground">/{r.total_max}</span></p>
              <p className="text-sm text-muted-foreground">{pct}%</p>
            </div>
          )}
        </div>

        {script.status !== "evaluated" && (
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <ScannedPanel />
            <div className="bg-background border border-border rounded-lg p-8 text-center flex flex-col items-center justify-center">
              <Brain className="h-10 w-10 text-primary mb-3" />
              <p className="font-heading font-bold text-lg">
                {pending
                  ? "Evaluation in progress"
                  : script.status === "failed"
                    ? studentRetryUsed ? "Evaluation failed again" : "Evaluation failed. You can retry once."
                    : "Not evaluated yet"}
              </p>
              {pending && <p className="text-sm text-muted-foreground mt-2">You can leave this page. We will alert you when your result is ready.</p>}
              {studentRetryUsed ? (
                <p className="text-sm text-muted-foreground mt-2">Your one retry has been used. Please contact support for help.</p>
              ) : (
                <div className="flex gap-2 mt-4">
                  <Button className="rounded-full gap-2" onClick={evaluate} disabled={evaluating || pending} data-testid="evaluate-now-button">
                    {evaluating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />} {script.status === "failed" ? "Retry evaluation" : "Evaluate now"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {r && (
          <>
            <Progress value={pct} className="mt-6" />

            {script.notifications && (
              <div className="flex flex-wrap gap-3 mt-6" data-testid="notification-status">
                <Badge variant="outline" className="gap-1.5 py-1.5">
                  <Mail className="h-3.5 w-3.5" /> Email {script.notifications.email?.mocked ? "(mocked)" : script.notifications.email?.status}
                </Badge>
                {/* <Badge variant="outline" className="gap-1.5 py-1.5">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp {script.notifications.whatsapp?.mocked ? "(mocked)" : script.notifications.whatsapp?.status}
                </Badge> */}
              </div>
            )}

            {/* Answer overlay: scanned pages with mistake markers beside the feedback */}
            <div className="grid lg:grid-cols-2 gap-6 mt-6 items-start">
              <div className="lg:sticky lg:top-24">
                <AnnotatedScript scriptId={scriptId} annotations={r.annotations || []} />
              </div>
              <div className="space-y-4">
                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-5 w-5 text-primary" />
                    <p className="overline text-muted-foreground">Overall feedback</p>
                  </div>
                  <p className="leading-relaxed">{r.overall_feedback}</p>
                </div>

                {(r.questions || []).map((q, i) => (
                  <div key={i} data-testid={`question-result-${q.number}`} className="bg-background border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading font-bold text-lg">Question {q.number}</h3>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => vote(q.number, "up")} data-testid={`like-${q.number}`} className={`p-1.5 rounded-full transition-colors ${feedback[String(q.number)] === "up" ? "bg-[hsl(var(--chart-2))]/20 text-[hsl(var(--chart-2))]" : "text-muted-foreground hover:bg-secondary"}`}>
                            <ThumbsUp className="h-4 w-4" />
                          </button>
                          <button onClick={() => vote(q.number, "down")} data-testid={`dislike-${q.number}`} className={`p-1.5 rounded-full transition-colors ${feedback[String(q.number)] === "down" ? "bg-destructive/20 text-destructive" : "text-muted-foreground hover:bg-secondary"}`}>
                            <ThumbsDown className="h-4 w-4" />
                          </button>
                        </div>
                        <span className="font-heading font-black text-xl text-primary">{q.awarded_marks}<span className="text-sm text-muted-foreground">/{q.max_marks}</span></span>
                      </div>
                    </div>
                    {q.student_answer_summary && (
                      <p className="text-sm text-muted-foreground mt-2 italic">"{q.student_answer_summary}"</p>
                    )}
                    <div className="mt-4 space-y-3">
                      <div className="bg-secondary/50 rounded-md p-4">
                        <div className="flex items-center gap-2 mb-1.5"><BookOpen className="h-4 w-4 text-primary" /><p className="overline text-muted-foreground">Why these marks</p></div>
                        <p className="text-sm leading-relaxed">{q.explanation}</p>
                      </div>
                      {(q.mistakes || []).length > 0 && (
                        <div className="bg-destructive/10 rounded-md p-4" data-testid={`mistakes-${q.number}`}>
                          <div className="flex items-center gap-2 mb-1.5"><AlertTriangle className="h-4 w-4 text-destructive" /><p className="overline text-muted-foreground">Mistakes</p></div>
                          <ul className="text-sm leading-relaxed list-disc pl-5 space-y-1 text-destructive">
                            {q.mistakes.map((m, mi) => <li key={mi}>{m}</li>)}
                          </ul>
                        </div>
                      )}
                      <div className="bg-[hsl(var(--chart-3))]/10 rounded-md p-4">
                        <div className="flex items-center gap-2 mb-1.5"><Lightbulb className="h-4 w-4 text-[hsl(var(--chart-3))]" /><p className="overline text-muted-foreground">Guidance</p></div>
                        <p className="text-sm leading-relaxed">{q.guidance}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
